import { useEffect, useRef, useState } from "react";
import {
  fetchEmail,
  fetchEmailThread,
  fetchEmailAttachmentBlob,
  fetchDealWorkdrive,
  saveEmailAttachmentToWorkdrive,
  searchDeals,
  setEmailRead,
  deleteEmail,
  ApiError,
  type EmailDetail,
  type EmailThread,
  type ReplyResult,
  type DealSearchHit,
} from "../../api";
import type { WdFile } from "@edge/shared";
import type { EmailAttachmentMeta } from "../../types/email";
import { EmailReplyComposer } from "../email/EmailReplyComposer";
import { EmailForwardComposer } from "../email/EmailForwardComposer";
import { EmailAssociationBanner } from "../email/EmailAssociationBanner";

/** Human-readable byte size for the attachment chips. */
function fmtSize(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Above this many recipients, collapse to the first few + a "+N more" toggle
 *  (mirrors Outlook) so a lender blast's 30+ addresses can't take over the
 *  modal and bury the email body. */
const RECIP_COLLAPSE_AT = 3;

function fmtRecip(r: { name: string; email: string }): string {
  return r.name ? `${r.name} <${r.email}>` : r.email;
}

/**
 * One To/Cc row. Collapses long recipient lists to the first few with an
 * expand toggle; when expanded, the full list scrolls within its own capped
 * box so it still can't crowd out the body.
 */
function RecipientRow({
  label,
  recipients,
}: {
  label: string;
  recipients: Array<{ name: string; email: string }>;
}) {
  const [expanded, setExpanded] = useState(false);
  if (recipients.length === 0) return null;

  const overflow = recipients.length - RECIP_COLLAPSE_AT;
  const collapsed = !expanded && overflow > 0;
  const shown = collapsed ? recipients.slice(0, RECIP_COLLAPSE_AT) : recipients;

  return (
    <div className="email-detail-row">
      <span className="email-detail-label">{label}</span>
      <span className="email-detail-value">
        <span className={collapsed ? undefined : "email-detail-recips-full"}>
          {shown.map(fmtRecip).join(", ")}
          {collapsed && "…"}
        </span>
        {collapsed ? (
          <button
            className="email-recip-toggle"
            onClick={() => setExpanded(true)}
          >
            +{overflow} more ▾
          </button>
        ) : overflow > 0 ? (
          <button
            className="email-recip-toggle"
            onClick={() => setExpanded(false)}
          >
            ▴ show less
          </button>
        ) : null}
      </span>
    </div>
  );
}

interface EmailDetailModalProps {
  messageId: string;
  onClose: () => void;
  /**
   * Fired when an action inside the modal changes deal-scoped state — a reply
   * sent, or the thread's deal link changed via the association banner. Lets a
   * deal-scoped host (the per-deal Email tab) refresh so a re-pinned-away email
   * doesn't linger as a ghost row. Optional: the feed/workspace don't need it.
   */
  onMutated?: () => void;
  /**
   * Render the FULL conversation (all messages, both directions) instead of the
   * single opened message. Used by the workspace, whose inbox-folder-scoped list
   * otherwise shows only the one inbound copy. The feed/per-deal tab leave this
   * off — their lists already surface every message in the thread.
   */
  conversationView?: boolean;
  /**
   * EM-4: when set (the per-deal Email tab passes it), attachments get a "Save
   * to WorkDrive" action that files them into this deal's folder. Omitted in the
   * non-deal cockpit feed/workspace, where the action wouldn't make sense.
   */
  dealId?: string;
}

const FULL_DATE_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "America/Los_Angeles",
});

/**
 * Full email body modal. Fetches /api/email/:id on mount — not pre-fetched
 * because email bodies can be large (forwarded threads, marketing mail).
 *
 * Escape and the X button close. Backdrop click does NOT close (consistent
 * with the WriteConfirm boundary pattern — destructive-feeling dismissal
 * needs an explicit intent).
 */
export function EmailDetailModal({
  messageId,
  onClose,
  onMutated,
  conversationView,
  dealId,
}: EmailDetailModalProps) {
  const [data, setData] = useState<EmailDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [attError, setAttError] = useState<string | null>(null);
  // EM-4 — save-to-WorkDrive. Which attachment's picker is open; the target deal
  // (auto = the dealId prop on the deal Email tab, else picked here in the
  // cockpit); that deal's lazily-loaded subfolders; the in-flight save; and
  // per-attachment success text.
  const [pickerAtt, setPickerAtt] = useState<string | null>(null);
  const [chosenDeal, setChosenDeal] = useState<{ id: string; name: string } | null>(null);
  const [dealQuery, setDealQuery] = useState("");
  const [dealHits, setDealHits] = useState<DealSearchHit[]>([]);
  const [dealSearching, setDealSearching] = useState(false);
  const dealReqRef = useRef(0);
  const [wdFolders, setWdFolders] = useState<WdFile[] | null>(null);
  const [wdFoldersFor, setWdFoldersFor] = useState<string | null>(null);
  const [wdFoldersErr, setWdFoldersErr] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedTo, setSavedTo] = useState<Record<string, string>>({});
  const [saveErr, setSaveErr] = useState<string | null>(null);
  // EM-6 — the deal this email is LINKED to (from the association banner). When
  // opened from the all-inbox cockpit panel (no explicit dealId), this is what
  // auto-scopes Save-to-WorkDrive / From-docs.
  const [linkedDeal, setLinkedDeal] = useState<{ id: string; name: string } | null>(null);

  // Effective deal for the deal-scoped actions, in priority order: the host-
  // provided deal (deal tab) → the email's linked deal → a deal picked here.
  const activeDealId = dealId ?? linkedDeal?.id ?? chosenDeal?.id ?? null;
  // Deal-search step shows only when there's no deal at all to act against.
  const needsDealPick = !activeDealId;
  const [replying, setReplying] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [sent, setSent] = useState<ReplyResult | null>(null);
  const [thread, setThread] = useState<EmailThread | null>(null);
  // QW-5 / QW-6. readOverride: null = use the fetched value; else the toggled one.
  const [readOverride, setReadOverride] = useState<boolean | null>(null);
  const [readBusy, setReadBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const isRead = readOverride ?? data?.isRead ?? false;

  async function handleToggleRead() {
    if (!data) return;
    const next = !isRead;
    setReadBusy(true);
    setActionError(null);
    try {
      await setEmailRead(data.id, next);
      setReadOverride(next);
      onMutated?.(); // refresh the list's unread indicator
    } catch {
      setActionError(`Couldn’t mark ${next ? "read" : "unread"}.`);
    } finally {
      setReadBusy(false);
    }
  }

  async function handleDelete() {
    if (!data) return;
    setDeleting(true);
    setActionError(null);
    try {
      await deleteEmail(data.id);
      onMutated?.(); // drop it from the list
      onClose();
    } catch {
      setDeleting(false);
      setActionError("Couldn’t delete. Try again.");
    }
  }

  async function downloadAttachment(att: EmailAttachmentMeta) {
    if (!data) return;
    setDownloading(att.id);
    setAttError(null);
    try {
      const blob = await fetchEmailAttachmentBlob(data.id, att.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setAttError(att.name);
    } finally {
      setDownloading(null);
    }
  }

  // EM-4 — load a deal's top-level subfolders for the folder picker (cached per
  // deal so re-opening / multiple attachments don't re-fetch).
  async function loadFolders(forDealId: string) {
    if (wdFoldersFor === forDealId && (wdFolders || wdFoldersErr)) return;
    setWdFolders(null);
    setWdFoldersErr(false);
    setWdFoldersFor(forDealId);
    try {
      const r = await fetchDealWorkdrive(forDealId);
      setWdFolders((r.files ?? []).filter((f) => f.isFolder));
    } catch {
      setWdFoldersErr(true);
    }
  }

  // EM-4 — open/close an attachment's picker. If the deal is already known
  // (deal tab, or a deal previously picked in this modal), preload its folders.
  function openSavePicker(attId: string) {
    setSaveErr(null);
    const opening = pickerAtt !== attId;
    setPickerAtt(opening ? attId : null);
    if (opening && activeDealId) void loadFolders(activeDealId);
  }

  // EM-4 (cockpit) — a deal was picked from the search; load its folders next.
  function chooseDeal(hit: DealSearchHit) {
    setChosenDeal({ id: hit.id, name: hit.name });
    void loadFolders(hit.id);
  }

  // EM-4 — save an attachment into the chosen folder (null = deal root).
  async function saveToWorkdrive(
    att: EmailAttachmentMeta,
    folder: { id: string | null; name: string },
  ) {
    if (!data || !activeDealId) return;
    const key = `${att.id}:${folder.id ?? "root"}`;
    setSavingKey(key);
    setSaveErr(null);
    try {
      await saveEmailAttachmentToWorkdrive(
        data.id,
        att.id,
        activeDealId,
        folder.id ?? undefined,
      );
      // In the cockpit, prefix the deal so it's clear where it landed.
      // Show the deal name in the confirmation unless it was the explicit host deal.
      const dealName = chosenDeal?.name ?? linkedDeal?.name;
      const label = dealId || !dealName ? folder.name : `${dealName} / ${folder.name}`;
      setSavedTo((m) => ({ ...m, [att.id]: label }));
      setPickerAtt(null);
    } catch (err) {
      setSaveErr(
        err instanceof ApiError && err.code === "no_workdrive_folder"
          ? "This deal has no WorkDrive folder."
          : `Couldn’t save ${att.name}.`,
      );
    } finally {
      setSavingKey(null);
    }
  }

  useEffect(() => {
    let cancelled = false;
    fetchEmail(messageId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? `${err.code} (HTTP ${err.status})`
            : err instanceof Error
              ? err.message
              : "fetch_failed",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [messageId]);

  // EM-4 (cockpit) — debounced deal search, only while the deal-pick step is
  // open. Stale-response guarded (mirrors the association banner).
  useEffect(() => {
    if (pickerAtt === null || !needsDealPick) return;
    const q = dealQuery.trim();
    if (q.length < 2) {
      setDealHits([]);
      setDealSearching(false);
      return;
    }
    setDealSearching(true);
    const myReq = ++dealReqRef.current;
    const t = setTimeout(async () => {
      try {
        const { deals } = await searchDeals(q, true); // include closed (escrow/funded)
        if (myReq === dealReqRef.current) setDealHits(deals);
      } catch {
        if (myReq === dealReqRef.current) setDealHits([]);
      } finally {
        if (myReq === dealReqRef.current) setDealSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [dealQuery, pickerAtt, needsDealPick]);

  // Conversation view — once we know the conversationId, pull the WHOLE thread
  // (both directions). Non-fatal: on failure we fall back to the single body.
  useEffect(() => {
    if (!conversationView || !data?.conversationId) return;
    let cancelled = false;
    fetchEmailThread(data.conversationId)
      .then((t) => {
        if (!cancelled) setThread(t);
      })
      .catch(() => {
        /* fall back to single-message body */
      });
    return () => {
      cancelled = true;
    };
  }, [conversationView, data?.conversationId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card email-detail-modal-card">
        <div className="email-detail-header">
          <div className="write-confirm-title">EMAIL</div>
          <button
            className="email-detail-close"
            onClick={onClose}
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>

        {!data && !error && (
          <div className="email-detail-loading">
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-label">LOADING EMAIL</span>
          </div>
        )}

        {error && (
          <div className="write-confirm-error">
            ⚠ Failed to load email: {error}
          </div>
        )}

        {data && (
          <>
            <div className="email-detail-meta">
              <div className="email-detail-row">
                <span className="email-detail-label">From</span>
                <span className="email-detail-value">
                  {data.fromName}{" "}
                  <span className="email-detail-addr">
                    &lt;{data.fromEmail}&gt;
                  </span>
                </span>
              </div>
              <RecipientRow label="To" recipients={data.toRecipients} />
              <RecipientRow label="Cc" recipients={data.ccRecipients ?? []} />
              <div className="email-detail-row">
                <span className="email-detail-label">Date</span>
                <span className="email-detail-value">
                  {FULL_DATE_FMT.format(new Date(data.receivedAt))}
                </span>
              </div>
              <div className="email-detail-row">
                <span className="email-detail-label">Subject</span>
                <span className="email-detail-value email-detail-subject">
                  {data.subject}
                </span>
              </div>
              {data.attachments && data.attachments.length > 0 ? (
                <div className="email-detail-attachments">
                  {data.attachments.map((att) => {
                    const saving = savingKey?.startsWith(`${att.id}:`) ?? false;
                    return (
                      <div key={att.id} className="email-att-row">
                        <button
                          className="email-detail-attachment"
                          onClick={() => downloadAttachment(att)}
                          disabled={downloading === att.id}
                          title={`Download ${att.name}`}
                        >
                          📎 {att.name}
                          {att.size ? (
                            <span className="email-detail-attachment-size">
                              {" "}
                              ({fmtSize(att.size)})
                            </span>
                          ) : null}
                          {downloading === att.id ? " …" : ""}
                        </button>
                        {savedTo[att.id] ? (
                          <span className="email-att-saved" title="Saved to WorkDrive">
                            ✓ Saved to {savedTo[att.id]}
                          </span>
                        ) : (
                          <div className="email-att-wd">
                            <button
                              className="email-att-wd-btn"
                              onClick={() => openSavePicker(att.id)}
                              disabled={saving}
                              title="Save to a deal's WorkDrive"
                            >
                              {saving ? "Saving…" : "⤓ Save to WorkDrive"}
                            </button>
                            {pickerAtt === att.id && !saving && (
                              <div className="email-att-wd-picker">
                                {needsDealPick ? (
                                  // Cockpit: pick the deal first.
                                  <>
                                    <input
                                      className="email-att-wd-search"
                                      autoFocus
                                      placeholder="Search a deal…"
                                      value={dealQuery}
                                      onChange={(e) => setDealQuery(e.target.value)}
                                    />
                                    {dealSearching ? (
                                      <div className="email-att-wd-empty">Searching…</div>
                                    ) : dealQuery.trim().length < 2 ? (
                                      <div className="email-att-wd-empty">
                                        Type 2+ characters
                                      </div>
                                    ) : dealHits.length === 0 ? (
                                      <div className="email-att-wd-empty">No deals</div>
                                    ) : (
                                      dealHits.map((h) => (
                                        <button
                                          key={h.id}
                                          className="email-att-wd-folder"
                                          onClick={() => chooseDeal(h)}
                                          title={h.name}
                                        >
                                          {h.name}
                                          {h.closed ? " (closed)" : ""}
                                        </button>
                                      ))
                                    )}
                                  </>
                                ) : (
                                  // Deal known (tab) or picked (cockpit): pick a folder.
                                  <>
                                    {chosenDeal && (
                                      <div className="email-att-wd-dealhdr">
                                        <span className="email-att-wd-dealname">
                                          {chosenDeal.name}
                                        </span>
                                        <button
                                          className="email-att-wd-change"
                                          onClick={() => {
                                            setChosenDeal(null);
                                            setWdFolders(null);
                                            setWdFoldersFor(null);
                                            setWdFoldersErr(false);
                                            setDealQuery("");
                                            setDealHits([]);
                                          }}
                                        >
                                          change
                                        </button>
                                      </div>
                                    )}
                                    {wdFoldersErr ? (
                                      <div className="email-att-wd-empty">
                                        Couldn’t load folders
                                      </div>
                                    ) : !wdFolders ? (
                                      <div className="email-att-wd-empty">Loading…</div>
                                    ) : (
                                      <>
                                        <button
                                          className="email-att-wd-folder"
                                          onClick={() =>
                                            void saveToWorkdrive(att, {
                                              id: null,
                                              name: "Deal root",
                                            })
                                          }
                                        >
                                          📁 Deal root
                                        </button>
                                        {wdFolders.map((f) => (
                                          <button
                                            key={f.id}
                                            className="email-att-wd-folder"
                                            onClick={() =>
                                              void saveToWorkdrive(att, {
                                                id: f.id,
                                                name: f.name,
                                              })
                                            }
                                          >
                                            📁 {f.name}
                                          </button>
                                        ))}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : data.hasAttachments ? (
                <div className="email-detail-attachments">
                  📎 Has attachments — view in Outlook
                </div>
              ) : null}
              {attError && (
                <div className="email-detail-att-error">
                  ⚠ Couldn’t download {attError}. Try “Open in Outlook”.
                </div>
              )}
              {saveErr && <div className="email-detail-att-error">⚠ {saveErr}</div>}
            </div>

            {data.conversationId && (
              <EmailAssociationBanner
                conversationId={data.conversationId}
                onChanged={onMutated}
                // EM-6: surface the linked deal so Save/From-docs auto-scope to it.
                onLinked={setLinkedDeal}
              />
            )}

            {conversationView && thread && thread.messages.length > 1 ? (
              <div className="email-detail-body email-thread">
                <div className="email-thread-count">
                  CONVERSATION · {thread.count} messages
                </div>
                {thread.messages.map((m) => (
                  <div
                    key={m.id}
                    className={`email-thread-msg ${m.direction === "outgoing" ? "is-out" : "is-in"}`}
                  >
                    <div className="email-thread-msg-head">
                      <span
                        className={`activity-dir ${m.direction === "outgoing" ? "out" : "in"}`}
                      >
                        {m.direction === "outgoing" ? "↗ OUT" : "↙ IN"}
                      </span>
                      <span className="email-thread-msg-from">{m.fromName}</span>
                      <span className="email-thread-msg-date">
                        {m.receivedAt ? FULL_DATE_FMT.format(new Date(m.receivedAt)) : ""}
                        {m.hasAttachments ? " · 📎" : ""}
                      </span>
                    </div>
                    <div className="email-thread-msg-body">{m.body}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="email-detail-body">{data.body}</div>
            )}

            {sent && (
              <div className="email-sent-banner">
                ✓ Reply sent
                {sent.redirected
                  ? ` — redirected to ${sent.sent_to.join(", ")} by the send guard (check your inbox). Intended: ${sent.intended_to.join(", ")}.`
                  : ` to ${sent.sent_to.join(", ")}.`}
              </div>
            )}

            <div className="email-detail-actions">
              <button
                className="write-confirm-confirm"
                onClick={() => setReplying(true)}
                disabled={!!sent}
              >
                ↩ REPLY
              </button>
              <button
                className="email-detail-action-btn"
                onClick={() => setForwarding(true)}
                disabled={!!sent}
                title="Forward this email (with its attachments)"
              >
                ↪ FORWARD
              </button>
              <button
                className="email-detail-action-btn"
                onClick={() => void handleToggleRead()}
                disabled={readBusy}
                title={
                  isRead
                    ? "Flag as unread (deal-with-later marker)"
                    : "Mark read in your mailbox"
                }
              >
                {readBusy ? "…" : isRead ? "✉ MARK UNREAD" : "✓ MARK READ"}
              </button>
              {data.webLink && (
                <a
                  className="email-detail-outlook"
                  href={data.webLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ↗ OPEN IN OUTLOOK
                </a>
              )}
              <button
                className="email-detail-action-btn email-detail-delete-btn"
                onClick={() => setConfirmDelete(true)}
                title="Move to Deleted Items (recoverable from Outlook)"
              >
                🗑 DELETE
              </button>
              <button className="email-detail-close-btn" onClick={onClose}>
                CLOSE
              </button>
            </div>
            {actionError && (
              <div className="email-detail-att-error">⚠ {actionError}</div>
            )}
          </>
        )}
      </div>

      {replying && data && (
        <EmailReplyComposer
          dealId={activeDealId ?? undefined}
          parent={{
            id: data.id,
            subject: data.subject,
            from: { name: data.fromName, email: data.fromEmail },
            to: data.toRecipients,
            cc: data.ccRecipients ?? [],
            body: data.body,
          }}
          onClose={() => setReplying(false)}
          onSent={(result) => {
            setSent(result);
            setReplying(false);
            onMutated?.();
          }}
        />
      )}

      {forwarding && data && (
        <EmailForwardComposer
          parent={{
            id: data.id,
            subject: data.subject,
            body: data.body,
            fromName: data.fromName,
            fromEmail: data.fromEmail,
            receivedAt: data.receivedAt,
          }}
          onClose={() => setForwarding(false)}
          onSent={(result) => {
            setSent(result);
            setForwarding(false);
            onMutated?.();
          }}
        />
      )}

      {confirmDelete && data && (
        <div className="write-confirm-backdrop nested-modal" role="dialog" aria-modal="true">
          <div className="write-confirm-card email-delete-confirm">
            <div className="write-confirm-title">DELETE EMAIL</div>
            <div className="write-confirm-body">
              Move this email to <b>Deleted Items</b>? It’s recoverable from
              Outlook’s trash — this clears it from your inbox, it’s not a
              permanent delete.
              <div className="email-delete-subject">{data.subject}</div>
            </div>
            <div className="email-detail-actions">
              <button
                className="email-detail-close-btn"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                CANCEL
              </button>
              <button
                className="write-confirm-confirm is-guarded"
                onClick={() => void handleDelete()}
                disabled={deleting}
              >
                {deleting ? "DELETING…" : "🗑 DELETE"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
